import type {
  Country,
  Language,
  Locale,
  SiteNamesMatchingClassification,
} from '@seek/melways-sites';
import type { Maybe } from 'graphql/jsutils/Maybe';

import type {
  AdType,
  AdvertiserHeaderContext,
  AnalyticsAdRatingScore,
  ContractType,
  Currency,
  CurrentAdvertiserContext,
  DashboardDraftFragment,
  DashboardJobFragment,
  IdentityHeaderContext,
  PermissionOptions,
  TalentSearchUserContext,
  UserHeaderContext,
} from 'src/graphql/generated';

export type Environment = 'development' | 'staging' | 'production';
export type TealiumEnvironment = 'dev' | 'qa' | 'prod';

export type EmployerSiteName = SiteNamesMatchingClassification<{
  product: 'employer';
}>;

export type HirerType = 'casual' | 'ad-pack' | 'contract';
export type PricingType = 'variable' | 'fixed';

export enum ConfigItemKey {
  VERSION = 'appVersion',
  ENV = 'environment',
  SITE = 'site',
  LOCALE = 'locale',
  FEATURES = 'features',
  IS_PROD = 'isProd',
  DEV_CONTEXT = 'devContext',
  IS_TRACKING_ENABLED = 'isTrackingEnabled',
  IS_CYPRESS_TEST = 'isCypressTest',
}

export interface Config {
  [ConfigItemKey.VERSION]: string;
  [ConfigItemKey.SITE]: EmployerSiteName;
  [ConfigItemKey.LOCALE]: Locale;
  [ConfigItemKey.ENV]: Environment;
  [ConfigItemKey.DEV_CONTEXT]?: DevContext;
  [ConfigItemKey.IS_TRACKING_ENABLED]: boolean;
  //  [ConfigItemKey.FEATURES]: ToggleItem;
}

export interface ConfigOptions {
  site: EmployerSiteName;
  locale: Locale;
  environment: Environment;
  appVersion: string;
  isProd: boolean;
  devContext?: DevContext;
  isTrackingEnabled: boolean;
  isCypressTest?: boolean;
}

export interface CorePageData {
  currentPage: string;
  siteSection: string;
  siteCountry?: Country;
  siteLanguage: Language;
  brand?: string;
  zone?: string;
}

export interface PageData extends CorePageData {
  hirerOriginZone?: Maybe<string>;
  seekAdvertiserId: string;
  advertiserAdCentreId: string;
  userId: string;
  isLoggedIn: boolean;
  isTestAccount?: Maybe<boolean>;
  isImpersonation?: boolean;
  advertiserCommitmentType: HirerType;
  pricingType: PricingType;
  isHirerRepeatAdPurchaser: boolean;
  hasPremiumTalentSearch: boolean;
  accountType?: string;
  isActivatedEmail: boolean;
  isAccountVerified?: Maybe<boolean>;
  accountPermissions: PermissionOptions;
  authenticationStatus: string;
  isAgency: boolean;
  isSwitchAccountEnabled?: boolean;
  isCreditOnHold: boolean;
  isTalentSearchApproved?: Maybe<boolean>;
  hasJobUpgradeButton: boolean;
  // These numbers represent the number of jobs ads shown on the dashboard (not the totals)
  adDraftShownCount: number;
  adLiveShownCount: number;
  adExpiredShownCount: number;
  adIndirectShownCount: number;
  adDirectShownCount: number;
  // These numbers represent the total number of jobs ads (max of 20, limited by AdCentre API)
  adDraftCount: number;
  adLiveCount: number;
  adExpiredCount: number;
  adIndirectCount: number;
  adDirectCount: number;
  // User identity (needed for Intercom)
  hirerUserEmail: string;
  hirerUserFullName: string;
}

export interface ErrorPageData {
  currentPage: string;
  siteSection: string;
  siteCountry?: Country;
  siteLanguage: Language;
  brand?: string;
  zone?: string;
  errorMessage: string;
}

export interface ClientContext {
  site: EmployerSiteName;
  locale: Locale;
  environment: Environment;
}

export interface DevContext {
  useLocalGraph: boolean;
  jobs: DashboardListItemFragment[];
}

export interface HeaderInfoFetchError {
  status?: number;
  message: string;
}

export type HeroMode = 'NewHirer' | 'ExistingHirer' | 'PTSHirer';

export type DashboardTalentSearchUserContext = Pick<
  TalentSearchUserContext,
  'claims' | 'productType'
> & {
  // Our custom convenience flags
  hasPTSClaim: boolean;
};

export interface DashboardAccountContext {
  currentAdvertiser: CurrentAdvertiserContext;
  emailVerified?: boolean;
  advertiser?: Maybe<AdvertiserHeaderContext>;
  identityContext?: Maybe<IdentityHeaderContext>;
  user?: Maybe<UserHeaderContext>;
}

export type DashboardJobKind = 'DRAFT' | 'DIRECT_LISTING' | 'INDIRECT_LISTING';

export interface AdvertiserContractTrackingData {
  advertiserContractType: ContractType[];
  advertiserContractIsDollarBased: boolean[];
  advertiserContractValue: number[];
  advertiserContractValueCurrency: Currency[];
  advertiserContractBalance: number[];
  advertiserContractAdType: AdType[];
}

export type ObjectInteraction = 'purchase-ad-packs';

export interface AnalyticsTrackingData {
  isPrimaryCTA?: boolean;
  seekListingId?: string;
  adDraftId?: string;
  adPosition?: number;
  candidateCount?: number;
  adHealth?: Maybe<AnalyticsAdRatingScore>;
  objectInteraction?: ObjectInteraction;
  articleTitle?: string;
  entryPoint?: string;
  alertShown?: BannerTrackingData;
  elementDesign?: string;
  elementText?: string;
  elementAction?: string;
  jobId?: string;
  nudgeType?: string;
}

export interface BannerTrackingData {
  type?: string;
  tone?: string;
  position?: string;
  title?: string;
  message?: string;
  messageCode?: string;
}

export interface CtaButtonProps {
  text: string;
  href: string;
  target?: string;
  trackingEventName: string;
  trackingActionOrigin?: string;
  trackingData?: AnalyticsTrackingData;
}

export type DashboardListItemFragment =
  | DashboardDraftFragment
  | DashboardJobFragment;
